'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { ArticleBlock } from './types';
import type { StrapiPagination, StrapiQuerySpec } from './strapi';

/**
 * Host adapter contract for blog-bound blocks and the blog system
 * pages. Hosts implement this against their CMS (a public WsEndPoint
 * over {@code BlogItem} in {@code gobito_dump_git/web-base-parent/
 * modules/social/}, a Strapi instance, or any other source) and
 * mount {@link BlogSourceProvider} once near the root.
 *
 * <p>When no provider is mounted, {@link useBlogSource} returns
 * {@code null}. Consuming blocks should treat that as "backend not
 * wired yet" and render an inline placeholder rather than throwing —
 * matches the {@code soft_fail_boot} memory.
 *
 * <h3>Shape</h3>
 *
 * Ported verbatim from ThemeMaker's {@code lib/app/cms.ts}. Hosts
 * with richer per-article data widen the {@link Article} interface
 * via TypeScript declaration merging.
 */

/**
 * Article shape consumed by every blog block and the
 * {@code BlogDetailLayout} system page. {@code blocks?} threads the
 * site-builder's per-article block overrides
 * ({@code ArticleBlockOverride} entity) into the renderer.
 */
export interface Article {
  id: number;
  documentId?: string;
  slug: string;
  title: string;
  excerpt: string;
  body: string;
  /**
   * Rich HTML body, when the backing CMS stores markup rather than the
   * plain-text/markdown {@link body}. Detail layouts render this as
   * HTML and fall back to {@link body} when it's absent — without it a
   * CMS row's `<p>…</p>` markup reaches the reader as escaped text.
   */
  bodyHtml?: string;
  category: string;
  tags?: string[];
  author?: { name: string; role?: string };
  readTime?: string;
  publishedAt: string;
  updatedAt: string;
  cover?: string;
  /** Optional alt text. Article-card variants render
   *  {@code <img alt>} when present, decorative {@code <div>}
   *  otherwise. */
  coverAlt?: string;
  /** Article-level block overrides — rendered by the detail layout
   *  in placement zones (top, bottom, sidebar, inline-after-N). */
  blocks?: ArticleBlock[];
}

/**
 * Re-export of the {@link StrapiPagination} envelope used by the
 * listing methods. The runtime's existing {@code strapi.ts} helpers
 * already define the shape — adapters reuse it so hosts can pass
 * Strapi responses through unchanged.
 */
export type BlogPagination = StrapiPagination;

/** Query spec for {@link BlogSource.listArticles}. Mirrors
 *  {@link StrapiQuerySpec} so hosts can forward straight to Strapi
 *  or translate to their own query language. */
export type BlogQuerySpec = StrapiQuerySpec;

/** Unified category shape across content modules (blog, courses,
 *  micro, video). Hosts producing the blog flavour populate it from
 *  their {@code BlogItemCategory} entity. */
export interface BlogCategory {
  id: string | number;
  slug: string;
  name: string;
  description?: string;
  image?: string;
  icon?: string;
  count?: number;
  color?: string;
}

export interface BlogSource {
  /** Paginated article search. Default sort + page-size at the host
   *  implementation. Returns {@code items: []} + zero pagination on a
   *  404 (treated as "no results", not an error). */
  listArticles(spec?: BlogQuerySpec): Promise<{
    items: Article[];
    pagination: BlogPagination;
  }>;

  /** Single article by slug. Returns {@code null} when the article
   *  doesn't exist — the page route calls {@code notFound()} in that
   *  case. */
  getArticleBySlug(slug: string): Promise<Article | null>;

  /** Blog categories. Used by the explore page's filter. Empty array
   *  when the host has no taxonomy yet. */
  listCategories(): Promise<BlogCategory[]>;
}

const BlogSourceContext = createContext<BlogSource | null>(null);

export function BlogSourceProvider({
  value,
  children,
}: {
  value: BlogSource;
  children: ReactNode;
}) {
  return <BlogSourceContext.Provider value={value}>{children}</BlogSourceContext.Provider>;
}

/** Returns the host-provided source, or {@code null} when no
 *  provider is mounted. Consuming blocks should render an inline
 *  placeholder on null and on thrown errors from the methods. */
export function useBlogSource(): BlogSource | null {
  return useContext(BlogSourceContext);
}
